import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { APPROVAL_SEARCH_URL } from '@/constant/approval';
import { approvalSearchListFetcher } from '@/hooks/apiHooks/approval';
import {
  I18nText,
  Lang,
  normalizeLang,
  pickI18nText,
} from '@/utils/approval/i18nText';
import { RelatedApprovalItem } from '@/types/approval';

const SEARCH_LIST_SIZE = 20;
const DEBOUNCE_MS = 300;

/** ignoreDocumentIds 는 콤마 조인된 문자열 — 이 엔드포인트의 배열 쿼리 관례(`buildApprovalSearchUrl` 동일). 빈 값이면 파라미터 생략. */
const buildSearchUrl = (keyword: string, ignoreDocumentIds: string) => {
  const params = new URLSearchParams({
    keyword,
    listSize: String(SEARCH_LIST_SIZE),
    sortType: 'authoredAt',
    orderType: 'desc',
  });
  if (ignoreDocumentIds) {
    params.set('ignoreDocumentIds', ignoreDocumentIds);
  }
  return `${APPROVAL_SEARCH_URL}?${params.toString()}`;
};

/** 검색 응답 원본 */
type RawSearchItem = {
  docId: string;
  subject: string | I18nText | null | undefined;
  status?: string;
  author?: { name?: string | I18nText | null } | null;
  authoredAt: string;
};

const toRelatedItem = (a: RawSearchItem, lang: Lang): RelatedApprovalItem => ({
  docId: a.docId,
  subject: pickI18nText(a.subject, lang),
  status: a.status,
  author: { name: pickI18nText(a.author?.name, lang) },
  authoredAt: a.authoredAt,
});

/**
 * 연관품의 검색
 * - 입력 즉시 호출하지 않고 300ms debounce
 * - keyword 비어있으면 호출 skip (key null)
 * - authorPassportIds 등 다른 필터 미사용 — 전체 품의 대상 keyword 검색
 */
type UseSearchRelatedApprovalsOptions = {
  /** debounce 지연(ms). 미지정 시 기본 300ms. */
  debounceMs?: number;
  /** 검색 결과에서 서버 단계에서 빼야 하는 문서번호(자기 문서 등). undefined/빈 값은 파라미터 생략. */
  excludeDocumentIds?: (string | undefined | null)[];
};

const useSearchRelatedApprovals = (
  keyword: string,
  {
    debounceMs = DEBOUNCE_MS,
    excludeDocumentIds,
  }: UseSearchRelatedApprovalsOptions = {},
) => {
  const { i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);
  const trimmed = keyword.trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(trimmed), debounceMs);
    return () => clearTimeout(id);
  }, [trimmed, debounceMs]);

  // 호출부가 배열 리터럴을 넘겨도 매 렌더 새 참조라 SWR 키가 흔들리지 않게 문자열로 접어 둔다.
  const excludeKey = (excludeDocumentIds ?? []).filter(Boolean).join(',');

  const { data, error, isLoading, isValidating } = useSWR<RawSearchItem[]>(
    debounced ? buildSearchUrl(debounced, excludeKey) : null,
    approvalSearchListFetcher<RawSearchItem>,
  );

  const items = useMemo<RelatedApprovalItem[]>(
    () => (data ?? []).map((a) => toRelatedItem(a, lang)),
    [data, lang],
  );

  return {
    items,
    isLoading: isLoading || (!!debounced && trimmed !== debounced),
    isValidating,
    error,
    debouncedKeyword: debounced,
  };
};

export default useSearchRelatedApprovals;
